if (!customElements.get('scarfhold-pattern-directory')) {
  class ScarfholdPatternDirectory extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('[data-pattern-filter]'));
      this.cards = Array.from(this.querySelectorAll('[data-pattern-card]'));
      this.emptyState = this.querySelector('[data-pattern-empty]');
      this.cardAnimations = new Set();
      this.filterGeneration = 0;

      this.buttons.forEach((button) => {
        button.addEventListener('click', this.onFilterClick);
      });
    }

    disconnectedCallback() {
      this.buttons?.forEach((button) => {
        button.removeEventListener('click', this.onFilterClick);
      });
      this.finishCardAnimations();
    }

    onFilterClick = async (event) => {
      const selectedButton = event.currentTarget;
      const selectedValue = selectedButton.dataset.patternFilter;
      const transition = this.dataset.filterTransition || 'none';

      this.buttons.forEach((button) => {
        const active = button === selectedButton;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });

      if (transition === 'none' || this.prefersReducedMotion() || !this.supportsWebAnimations()) {
        this.finishCardAnimations();
        this.filterGeneration += 1;
        this.applyFilter(selectedValue);
        return;
      }

      const generation = ++this.filterGeneration;
      this.finishCardAnimations();
      const previousRects = this.captureVisibleCardRects();
      const nextCards = this.cards.filter((card) => this.cardMatches(card, selectedValue));
      const nextCardSet = new Set(nextCards);
      const exitingCards = this.cards.filter((card) => !card.hidden && !nextCardSet.has(card));

      await Promise.all(exitingCards.map((card) => this.animateExit(card, transition)));
      if (generation !== this.filterGeneration || !this.isConnected) return;

      this.applyFilter(selectedValue);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (generation !== this.filterGeneration || !this.isConnected) return;
          this.animateVisibleCards(nextCards, previousRects, transition);
        });
      });
    };

    cardMatches(card, selectedValue) {
      return selectedValue === 'all' || card.dataset.patternSupercategory === selectedValue;
    }

    applyFilter(selectedValue) {
      let visibleCount = 0;
      this.cards.forEach((card) => {
        const visible = this.cardMatches(card, selectedValue);
        card.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      if (this.emptyState) this.emptyState.hidden = visibleCount !== 0;
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    supportsWebAnimations() {
      return typeof Element.prototype.animate === 'function';
    }

    transitionDuration() {
      const rawDuration = getComputedStyle(this).getPropertyValue('--pattern-filter-transition-duration');
      return Number.parseFloat(rawDuration) || 400;
    }

    moveDistance() {
      const rawDistance = getComputedStyle(this).getPropertyValue('--pattern-filter-move-distance');
      return Number.parseFloat(rawDistance) || 0;
    }

    captureVisibleCardRects() {
      return new Map(
        this.cards
          .filter((card) => !card.hidden)
          .map((card) => [card.dataset.patternId, card.getBoundingClientRect()])
      );
    }

    trackCardAnimation(animation, card) {
      this.cardAnimations.add(animation);
      card.style.willChange = 'transform, opacity';
      const clean = () => {
        this.cardAnimations.delete(animation);
        card.style.willChange = '';
      };
      animation.addEventListener('finish', clean, { once: true });
      animation.addEventListener('cancel', clean, { once: true });
      return animation.finished.catch(() => undefined);
    }

    finishCardAnimations() {
      this.cardAnimations?.forEach((animation) => animation.cancel());
      this.cardAnimations?.clear();
      this.cards?.forEach((card) => {
        card.style.willChange = '';
      });
    }

    animateExit(card, transition) {
      const keyframes = transition === 'fade_move'
        ? [
            { opacity: 1, transform: 'translateY(0)' },
            { opacity: 0, transform: `translateY(${-this.moveDistance()}px)` },
          ]
        : [{ opacity: 1 }, { opacity: 0 }];
      const animation = card.animate(keyframes, {
        duration: Math.min(200, this.transitionDuration() * 0.35),
        easing: 'ease-in',
      });
      return this.trackCardAnimation(animation, card).then(() => animation.cancel());
    }

    animateVisibleCards(cards, previousRects, transition) {
      const duration = this.transitionDuration();
      const moveDistance = this.moveDistance();

      cards.forEach((card) => {
        const previousRect = previousRects.get(card.dataset.patternId);
        let keyframes;

        if (transition === 'fade_move' && previousRect) {
          const currentRect = card.getBoundingClientRect();
          const deltaX = previousRect.left - currentRect.left;
          const deltaY = previousRect.top - currentRect.top;
          if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) return;
          keyframes = [
            { transform: `translate(${deltaX}px, ${deltaY}px)` },
            { transform: 'translate(0, 0)' },
          ];
        } else if (transition === 'fade_move') {
          keyframes = [
            { opacity: 0, transform: `translateY(${moveDistance}px)` },
            { opacity: 1, transform: 'translateY(0)' },
          ];
        } else {
          keyframes = [{ opacity: 0 }, { opacity: 1 }];
        }

        const animation = card.animate(keyframes, {
          duration,
          easing: transition === 'fade_move' ? 'cubic-bezier(0.22, 1, 0.36, 1)' : 'ease-out',
        });
        this.trackCardAnimation(animation, card);
      });
    }
  }

  customElements.define('scarfhold-pattern-directory', ScarfholdPatternDirectory);
}
